import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import type { SoftwareManagementSoftwareInfoSoftwareSetupFragment } from 'generated/resources/graphql'
import { SoftwareManagementApplicationsListPageQueryDocument } from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'

import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { matchText } from 'modules/core/lib/stringUtils'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import InitialSetup from 'modules/software_management/components/InitialSetup'
import SoftwareSetupsList from 'modules/software_management/components/SoftwareSetupsList'
import LowFundsNotification from 'modules/software_management/components/_Notifications/LowFundsNotification'
import { getSoftware } from 'modules/software_management/lib/software'

import SoftwareDashboard from 'modules/software_management/components/SoftwareDashboard'
import { TerminatedEmployeesCard } from 'modules/software_management/components/TerminatedEmployeesCard'
import { startOfYear } from 'modules/software_management/lib/dates'
import isEmployeeTerminated from 'modules/software_management/lib/employeeIsTerminated'
import { useApplicationsContext } from './ApplicationsPage'

const useSearchFilter = createComputed(
  (search: string, softwareSetups: SoftwareManagementSoftwareInfoSoftwareSetupFragment[]) => {
    return softwareSetups.filter((softwareSetup) => {
      const software = getSoftware(softwareSetup)

      return matchText(search, software.name)
    })
  }
)
type directionType = 'asc' | 'desc'

const sortSoftwareSetups = createComputed(
  (
    softwareSetups: SoftwareManagementSoftwareInfoSoftwareSetupFragment[],
    attribute: string,
    direction: directionType | undefined
  ) => {
    if (direction) {
      return orderBy(
        softwareSetups,
        (softwareSetup: SoftwareManagementSoftwareInfoSoftwareSetupFragment) => {
          switch (attribute) {
            case 'name':
              return softwareSetup.software?.name
            case 'date':
              return softwareSetup.createdAt
            case 'employees':
              return softwareSetup.employeeSoftwareMemberships.length
          }
        },
        [direction]
      )
    } else {
      return softwareSetups
    }
  }
)

const ApplicationsListPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee } = useCurrentSession()
  const [direction, setDirection] = React.useState<directionType | undefined>('asc')

  const [attribute, setAttribute] = React.useState<string>('name')

  const { currency, bankAccount } = useApplicationsContext()

  const { data, loading, error } = useQuery(SoftwareManagementApplicationsListPageQueryDocument, {
    variables: {
      dateFrom: dateToString(startOfYear(), 'backend'),
      currency,
      employeeId: Number(employee?.id),
      creationSource: 'software',
    },
    skip: !employee,
  })

  const softwareSetups = data?.softwareManagement.softwareSetupsConnection.nodes || []
  const dashboardCosts = data?.softwareManagement.dashboardSoftwareCostsConnection.nodes || []
  const extendedEmployee = data?.employees.employee

  const terminatedEmployeeIds = softwareSetups
    .flatMap((softwareSetup) =>
      softwareSetup.employeeSoftwareMemberships.map((membership) => membership.employee)
    )
    .filter((employee) => isEmployeeTerminated(employee.terminatedOn))
    .map((Employee) => Employee.id)

  const uniqueTerminatedEmployeesCount = new Set(terminatedEmployeeIds).size

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const searchedSoftwareSetups = useSearchFilter(debouncedSearch, softwareSetups).get()
  const filteredSoftwareSetups = sortSoftwareSetups(
    searchedSoftwareSetups,
    attribute,
    direction
  ).get()

  const showInitialSetup = softwareSetups.length === 0

  useEffect(() => {
    track(trackingEvents.softwareManagement.goToSoftwareSetupsList)
  }, [])

  const handleSoftwareCreated = (setupId: number) => {
    SafeLocalStorage.setItem(LOCAL_STORAGE_KEYS.softwareEmployeeSelector, null)
    navigate(navigation.software.applications.applicationId.fullPath({ application_id: setupId }))
  }

  return (
    <ResponsivePage.Body
      variant="wide"
      loading={loading}
      error={error}
      showHeader={!showInitialSetup}
      widget={
        <Box flexDirection="column" gap="s8" width="full">
          {uniqueTerminatedEmployeesCount > 0 && (
            <TerminatedEmployeesCard employeesCount={uniqueTerminatedEmployeesCount} />
          )}
          {bankAccount &&
            bankAccount.availableBalanceCents < bankAccount.lowBalanceThresholdCents && (
              <LowFundsNotification />
            )}
        </Box>
      }
    >
      {showInitialSetup && extendedEmployee ? (
        <InitialSetup employee={extendedEmployee} onSoftwareCreated={handleSoftwareCreated} />
      ) : (
        <Box gap="s24">
          <SoftwareDashboard
            softwareSetups={softwareSetups}
            dashboardCosts={dashboardCosts}
            currency={currency}
          />
          {extendedEmployee && (
            <SoftwareSetupsList
              softwareSetups={filteredSoftwareSetups}
              currency={currency}
              attribute={attribute}
              setAttribute={setAttribute}
              direction={direction}
              setDirection={setDirection}
              search={search}
              onSearch={onSearch}
              employee={extendedEmployee}
              onSoftwareCreated={handleSoftwareCreated}
            />
          )}
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default ApplicationsListPage
